import { ElementType, HTMLAttributes, forwardRef } from "react";
import { cn } from "@drpcrd/utils/shared";

export type ChipProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  href?: string;
  target?: string;
};

/**
 * A small pill-shaped chip used to display labels, categories, or tags.
 * Supports polymorphic rendering via the `as` prop — pass a `Link` component
 * to make it navigable.
 */
export const Chip = forwardRef<HTMLElement, ChipProps>(
  ({ className, children, as, ...props }, ref) => {
    if (props.href && !as) {
      if (process.env.NODE_ENV !== "production") {
        throw new Error(
          "You must set the `as` prop to Next.js `Link` when providing an `href` prop to Chip",
        );
      }
    }

    const Comp = as ?? "div";

    return (
      <Comp
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1 rounded-lg border border-neutral-500 bg-white px-2 py-1",
          "text-sm font-semibold text-text-body",
          as && "cursor-pointer transition-colors hover:bg-neutral-50",
          className,
        )}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Chip.displayName = "Chip";
